/**
 * FloatingSelectionBar — shared primitive for the bottom-centered floating
 * selection/action pill (BLU-680; sibling of PageActionBar in the layout
 * toolkit).
 *
 * Exists because three surfaces (production OrdersPage, product-dev
 * BatchActionBar, Products) hand-rolled the same
 * `fixed bottom-N left-1/2 (centred) z-N … safe-area` wrapper and kept
 * diverging on the parts that matter:
 *   - A11Y CONTRACT (baked in, non-optional): an sr-only
 *     `role="status" aria-live="polite"` node announcing `srStatus` — a
 *     selection can change by many at once (shift-click), and a visible chip
 *     alone is silent to screen readers.
 *   - MOTION CONTRACT (baked in): AnimatePresence enter/exit using the shared
 *     STICKY_BUTTON animation with reduced-motion handling — visibility-driven
 *     consumers get a matched exit for free. (A consumer whose PARENT
 *     conditionally mounts it keeps today's no-exit behavior — the presence
 *     boundary must live where mounting is decided.)
 *
 * VISUAL SKIN STAYS WITH THE CALLER — the consumers span two design
 * languages, so this primitive carries structure, a11y, and motion only.
 * `pillClassName` fully replaces the default pill skin when provided.
 *
 * `skin="wrap"` is the one shared alternative skin, for an inventory pill with
 * three or more actions (Products > Client prices, Stock Sync's Skip or
 * Always 0 list). The wrapper is pinned at `left: 50%`, so on its own it is
 * only ever half the window wide: at 375 px the default `rounded-full` pill
 * stacked every button into a tall white circle. The wrap skin sizes the
 * wrapper to the pill (`w-max`, capped at the window less 1rem a side); below
 * `sm` the pill is a rounded rectangle whose buttons wrap in rows, from `sm`
 * up the same pill as the default. The skin also shapes the pill's buttons
 * (round from `sm` up, tighter below it), so a caller passes plain buttons.
 */
import type { ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ANIMATIONS, getAnimationProps } from '@/lib/animations';
import { cn } from '@/lib/utils';

// max-w + wrap: a busy pill (counts + several actions) must never push its
// last action off-screen at narrow viewports — the trailing button in this
// bar is routinely the primary or destructive one (caught live at 1280px).
const DEFAULT_PILL_CLASS =
  'flex max-w-[calc(100vw-2rem)] flex-wrap items-center justify-center gap-1.5 rounded-full border bg-card py-1.5 pl-4 pr-1.5 shadow-lg';

const WRAP_BAR_CLASS = 'w-max max-w-[calc(100vw-2rem)]';
const WRAP_PILL_CLASS =
  'flex max-w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-1 rounded-2xl border bg-card px-3 py-1.5 shadow-lg sm:rounded-full sm:pl-4 sm:pr-1.5 [&_button]:rounded-full max-sm:[&_button]:px-2.5 max-sm:[&_button]:text-xs';

export type FloatingSelectionBarSkin = 'default' | 'wrap';

interface FloatingSelectionBarProps {
  /** Drives enter/exit. Keep the component MOUNTED and toggle this for the
   *  exit animation to play. */
  visible: boolean;
  /** sr-only polite live-region text (e.g. "3 products selected"). Required —
   *  the a11y contract is the point of the primitive. */
  srStatus: string;
  /** Accessible name of the container. */
  ariaLabel: string;
  /** Landmark semantics. 'toolbar' for a flat action row; 'region' when the
   *  content is richer than a toolbar's arrow-key convention implies. */
  role?: 'toolbar' | 'region';
  /** Rendered ABOVE the pill (e.g. an option card riding with the actions). */
  accessory?: ReactNode;
  /** Pill content: count text, dividers, action buttons. */
  children: ReactNode;
  /** `wrap`: the shared narrow-screen skin (see the header). Ignored when `pillClassName` is set. */
  skin?: FloatingSelectionBarSkin;
  /** REPLACES the pill skin (rounded-full border bg-card shadow). */
  pillClassName?: string;
  /** Merged onto the fixed wrapper — override offset/z-index here. */
  className?: string;
}

export function FloatingSelectionBar({
  visible,
  srStatus,
  ariaLabel,
  role = 'toolbar',
  accessory,
  children,
  skin = 'default',
  pillClassName,
  className,
}: FloatingSelectionBarProps) {
  const shouldReduceMotion = useReducedMotion();
  const wrap = skin === 'wrap';

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          {...getAnimationProps(ANIMATIONS.stickyButton, shouldReduceMotion)}
          initial="initial"
          animate="animate"
          exit="exit"
          className={cn(
            'fixed bottom-8 left-1/2 z-50 flex flex-col items-center gap-3 pb-[env(safe-area-inset-bottom)]',
            wrap && WRAP_BAR_CLASS,
            className,
          )}
          // The -50% centring shift goes through framer, NOT Tailwind's
          // -translate-x-1/2: framer writes an inline `transform` for the y/scale
          // animation, which wipes Tailwind's transform and left the bar's LEFT
          // edge at the window centre. Framer composes `x` with y/scale.
          style={{ x: '-50%' }}
          role={role}
          aria-label={ariaLabel}
        >
          <span role="status" aria-live="polite" className="sr-only">
            {srStatus}
          </span>
          {accessory}
          <div className={pillClassName ?? (wrap ? WRAP_PILL_CLASS : DEFAULT_PILL_CLASS)}>{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
